import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'hooks';

import Header from 'components/header.js';
import Footer from 'components/footer.js';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import OPWBreadcrumb from 'components/breadcrumb.js';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { FileWithPath, useDropzone } from 'react-dropzone';
import { AiOutlineWarning, AiOutlineCheckCircle } from 'react-icons/ai';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { actionUploadYardWtFile } from './actions';
import Alert from 'react-bootstrap/Alert';
import { AxiosProgressEvent } from 'axios';
import { useNavigate } from 'react-router-dom';

const DROP_BOX_STYLE = {
	margin: '1rem',
	padding: '2rem',
	border: '3px #ddd dashed',
	borderRadius: '0.5rem',
	maxWidth: '500px',
	color: '#aaa',
};

export const WeightsUpload = () => {
	const clientId = useAppSelector((state) => state.clientId);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadFile, setUploadFile] = useState<FileWithPath | null>(null);
	const [fileUploadSuccess, setFileUploadSuccess] = useState(0);
	const navigate = useNavigate();

	const uploadProgressEvent = (e: AxiosProgressEvent) => {
		const progress = e.total ? (e.loaded / e.total) * 100 : 50;
		setUploadProgress(progress);
	};

	const onDrop = useCallback(
		(acceptedFiles: FileWithPath[]) => {
			if (!clientId || uploadFile !== null || acceptedFiles.length !== 1)
				return;

			setUploadFile(acceptedFiles[0]);
			const theFile = acceptedFiles[0];
			setFileUploadSuccess(0);
			actionUploadYardWtFile(
				theFile,
				theFile.name,
				clientId,
				uploadProgressEvent
			).then((uploadResult) => {
				if (uploadResult.success) {
					setFileUploadSuccess(1);
				} else {
					setFileUploadSuccess(2);
				}
			});
		},
		[clientId, uploadFile]
	);

	const { getRootProps, getInputProps, isDragActive, isDragAccept } =
		useDropzone({
			maxFiles: 1,
			accept: {
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
					'.xlsx',
				],
			},
			onDrop,
		});

	const message = useMemo(() => {
		if (!isDragActive) return 'Click here or drag-n-drop the xlsx file.';
		if (isDragAccept)
			return (
				<>
					<AiOutlineCheckCircle /> Drop here
				</>
			);
		return (
			<>
				<AiOutlineWarning />
				Only one xlsx file is permitted.
			</>
		);
	}, [isDragActive, isDragAccept]);

	useEffect(() => {
		if (!clientId) navigate('/manage/session');
	});

	if (fileUploadSuccess === 0) {
		if (uploadFile == null)
			return (
				<PageTemplate>
					<div {...getRootProps()} style={DROP_BOX_STYLE}>
						<input {...getInputProps()} />
						{message}
					</div>
				</PageTemplate>
			);

		return (
			<PageTemplate>
				<div style={DROP_BOX_STYLE}>
					<ProgressBar animated={uploadProgress < 100} now={uploadProgress} />
					{uploadProgress < 100
						? 'Uploading ' + uploadFile.path
						: 'Upload complete. Checking file.'}
				</div>
			</PageTemplate>
		);
	}
	if (fileUploadSuccess === 1) {
		return (
			<PageTemplate>
				<Alert variant={'success'}>
					<p style={{ textAlign: 'center' }}>
						File has been uploaded successfully.
					</p>
				</Alert>
			</PageTemplate>
		);
	}
	return (
		<PageTemplate>
			<Alert variant={'danger'}>
				<p style={{ textAlign: 'center' }}>
					File upload failed. Try again or contact Optiweigh Support.
				</p>
			</Alert>
		</PageTemplate>
	);
};

const PageTemplate = (props: { children: ReactNode }) => {
	return (
		<>
			<Header>
				<OPWBreadcrumb trail='[["Home", "/"], ["Weights upload", ""]]' />
			</Header>
			<Container fluid className="body">
				<Card body>
					<Card.Title>Weights Upload</Card.Title>
					<Card.Body>
						<p>
							This facility allows you to upload animal information such as:
							<ul>
								<li>Visual ID's to show on the individual animal reports;</li>
								<li>
									Weight records for inclusion in weight gain calculations; and
								</li>
								<li>
									Weight records to be used to compare your Optiweigh data with
									your yard weights or your sale weights.
								</li>
							</ul>
						</p>
						<p>
							Download and fill the{' '}
							<a href="/YardWtImportTemplate2.xlsx" download>
								Template spreadsheet
							</a>
							, then upload in the box below.
						</p>
						{props.children}
					</Card.Body>
				</Card>
			</Container>
			<Container fluid>
				<Row>
					<Col sm="12">
						<Footer />
					</Col>
				</Row>
			</Container>
		</>
	);
};
